import React, { useEffect, useState } from "react";
import { useScrollManagement } from "./useScrollManagement";
import type { AIEvent, AIResponse, Citation, SearchDocument } from "@/types";
import TypewriterMarkdown from "@/components/TypewriterMarkdown";
import { Button } from "@/components/ui/button.tsx";
import "../index.css";
import { ArrowsPointingInIcon, ArrowsPointingOutIcon } from "@heroicons/react/16/solid";
import { useTranslation } from "@/locales";
import { useIsMobile } from "@/hooks/use-mobile";

interface ThinkEventProps {
  event: AIEvent;
  citations?: Citation[];
  doc?: SearchDocument[];
  conversationNo?: string;
  isLatestMessage: boolean;
  isThink?: boolean;
  thinkStop?: boolean;
  messageGroup?: AIResponse;
  groupIndex?: number;
  setMessages: React.Dispatch<React.SetStateAction<AIResponse[]>>;
}

export const ThinkEvent = React.memo(
  ({
    event,
    citations,
    doc,
    isLatestMessage,
    isThink,
    thinkStop,
    messageGroup,
    groupIndex,
    setMessages,
    conversationNo,
  }: ThinkEventProps) => {
    const { t } = useTranslation();
    const { containerRef, scrollToBottom } = useScrollManagement();
    const [userScrolled, setUserScrolled] = useState(false);
    const [isCurrent, setIsCurrent] = useState(0);
    const [isEnd, setIsEnd] = useState(false);
    const isMobile = useIsMobile();

    useEffect(() => {
      if (!isThink) {
        setMessages((prev) => {
          const updatedMessages = [...prev];
          updatedMessages[groupIndex ?? 0] = {
            ...updatedMessages[groupIndex ?? 0],
            isOpen: false,
          };
          return updatedMessages;
        });
      }
    }, [isThink]);

    useEffect(() => {
      if (containerRef.current) {
        if (!userScrolled) {
          scrollToBottom();
        }
      }
    }, [isCurrent]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const handleScroll = () => {
        // 这里写滚动时的逻辑，比如判断是否到底部
        const { scrollTop, scrollHeight, clientHeight } = container;
        const isAtBottom = scrollHeight - scrollTop <= clientHeight + 30;
        setUserScrolled(!isAtBottom);
      };

      container.addEventListener("scroll", handleScroll);

      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }, [containerRef, messageGroup, messageGroup?.isOpen]);

    const onComputedEnd = () => {
      if (!isThink) {
        setIsEnd(true);
      } else {
        setIsEnd(false);
      }
    };

    useEffect(() => {
      console.log("Think ended?", isEnd);
      if (isEnd) {
        setMessages((prev) => {
          const updatedMessages = [...prev];
          updatedMessages[groupIndex ?? 0] = {
            ...updatedMessages[groupIndex ?? 0],
            isThinkEnd: true,
          };
          return updatedMessages;
        });
      }
    }, [isEnd]);

    const onDocClick = (doc: SearchDocument[]) => {
      console.log(doc);
      console.log(event);
    };

    console.log(doc);
    const isOpen = messageGroup?.isOpen;
    const thinkingContent = "thinking" in event.data ? event.data.thinking : t("chat.processing");
    // 如果思考已完成，直接显示全部内容，跳过打字效果
    const isThinkEnd = messageGroup?.isThinkEnd;

    return (
      <>
        {/* 始终渲染 TypewriterMarkdown 以保持状态，通过 CSS 控制显示/隐藏 */}
        <div style={{ display: isOpen ? "block" : "none" }}>
          <div className="pt-0 ai overflow-hidden ">
            <div
              className={
                isThink
                  ? "text-[14px] think-button-collapse collapse-collapse-button-Lf stick-LY collapse-collapse-button-in-update"
                  : "think-button-collapse collapse-collapse-button-Lf stick-LY"
              }
              onClick={(e) => {
                e.stopPropagation();
                console.log("stopPropagation");
                setMessages((prev) => {
                  const updatedMessages = [...prev];
                  updatedMessages[groupIndex ?? 0] = {
                    ...updatedMessages[groupIndex ?? 0],
                    isOpen: false,
                  };
                  return updatedMessages;
                });
              }}>
              <div className="flex justify-between items-center w-full">
                {isThink ? (
                  <div className="think-block-title light">
                {thinkStop ? t("chat.thinkingStopped") : t("chat.deepThinking")}
                  </div>
                ) : (
                  <div className="think-block-title">
                    {" "}
                {thinkStop ? t("chat.thinkingStopped") : t("chat.thinkingCompleted")}{" "}
                  </div>
                )}
                <Button
                  variant={isMobile ? "link" : "outline"}
                  className={"text-[var(--text-text_secondary, rgba(0, 0, 0, 0.6))]"}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log("stopPropagation");
                    setMessages((prev) => {
                      const updatedMessages = [...prev];
                      updatedMessages[groupIndex ?? 0] = {
                        ...updatedMessages[groupIndex ?? 0],
                        isOpen: false,
                      };
                      return updatedMessages;
                    });
                  }}>
                  <ArrowsPointingInIcon className="z-10 text-[22px] h-[16px] w-[16px] md:h-[28px] md:w-[28px] fill-[var(--text-text_primary)]" />
                </Button>
              </div>
            </div>
            <div
              className={isThink ? "think think-container" : "think think-container"}
              style={{ maxHeight: isThink ? "200px" : "100%" }}
              ref={containerRef}>
              <div className="content think-content">
                <TypewriterMarkdown
                  key={`think-${groupIndex}`}
                  content={thinkingContent}
                  conversationNo={conversationNo}
                  speed={5}
                  citations={citations}
                  docs={doc}
                  isLatestMessage={isLatestMessage && isThink && !isThinkEnd}
                  think={true}
                  isEnd={isThinkEnd}
                  onPDFClick={(pdf) => {
                    console.log(pdf);
                  }}
                  onUnderway={(num) => {
                    setIsCurrent(num ?? 0);
                  }}
                  onComplete={() => {
                    onComputedEnd();
                  }}
                />
                {/*<div ref={bottomRef}/>*/}
              </div>
              {/*<div ref={bottomRef}/>*/}
            </div>
            {/*<div ref={bottomRef}/>*/}
          </div>
        </div>
        {!isOpen && (
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              setMessages((prev) => {
                const updatedMessages = [...prev];
                updatedMessages[groupIndex ?? 0] = {
                  ...updatedMessages[groupIndex ?? 0],
                  isOpen: true,
                };
                return updatedMessages;
              });
            }}>
            <div>
              <span className={"text-[var(--text-text_secondary, rgba(0, 0, 0, 0.6))]"}>
                {`${
                  thinkStop
                    ? t("chat.thinkingStopped")
                    : isThink
                    ? t("chat.deepThinking")
                    : `${t("chat.thinkingCompleted")} (${t("common.duration")} ${Math.floor(
                        ("time" in event.data ? event.data.time ?? 0 : 0) / 1000
                      )} ${t("common.seconds")})`
                }`}
              </span>
            </div>
            <ArrowsPointingOutIcon />
          </Button>
        )}
      </>
    );
  }
);
